import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Table, Row, Col, Card, CardBody, Input, Badge,
    Pagination, PaginationItem, PaginationLink, FormGroup, Label,
    Modal, ModalHeader, ModalBody, ModalFooter, InputGroup, InputGroupText, Form
} from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const PaymentList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [payments, setPayments] = useState([]);
    const [summary, setSummary] = useState({ total_amount: 0, invoice_total: 0, standalone_total: 0 });
    const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1 });
    const [clients, setClients] = useState([]);

    const [modal, setModal] = useState(false);
    const [payData, setPayData] = useState({
        client_id: null,
        amount: "",
        payment_mode: "cash",
        note: ""
    });

    const [filters, setFilters] = useState({
        search: "", client_id: null, payment_type: null, page: 1, limit: 10
    });

    const paymentTypes = [
        { value: "invoice", label: "Invoice Settlements" },
        { value: "standalone", label: "Advances / General" }
    ];

    useEffect(() => {
        fetchClients();
        loadPayments();
    }, [filters.page, filters.client_id, filters.payment_type]);

    const fetchClients = async () => {
        // 🔥 Loading-ah start-laye true pannidalaam, appo thaan UI correct-ah irukkum
        dispatch(loadingTrue());
        try {
            const res = await Handler({ method: "GET", url: "/clients/list.php" });

            if (res?.success) {
                // 🔥 'c.client.name' structure-ah inga update panniten
                const clientOptions = res?.data?.map(c => ({
                    value: c?.id,
                    label: c?.client?.name?.toUpperCase() || "UNKNOWN CLIENT"
                })) || [];

                setClients(clientOptions);
            } else {
                console.error("Client fetch failed:", res?.message);
            }
        } catch (err) {
            console.error("Client fetch error:", err);
        } finally {
            // 🔥 Success aanaalum failure aanaalum loading-ah false panniduvoom
            dispatch(loadingFalse());
        }
    };

    const loadPayments = async () => {
        dispatch(loadingTrue());
        try {
            const params = {
                ...filters,
                client_id: filters?.client_id ? filters?.client_id?.value : "",
                payment_type: filters?.payment_type ? filters?.payment_type?.value : ""
            };
            const queryParams = new URLSearchParams(params).toString();
            const res = await Handler({ method: "GET", url: `/payments/list.php?${queryParams}` });
            if (res?.success) {
                setPayments(res?.data || []);
                setSummary(res?.summary || { total_amount: 0, invoice_total: 0, standalone_total: 0 });
                setPagination({
                    current_page: res.page,
                    total_pages: res.total_pages,
                    total_records: res.total
                });
                dispatch(loadingFalse());
            } else { dispatch(loadingFalse()); }
        } catch (err) { toast.error("Failed to load payments history"); }
        finally { dispatch(loadingFalse()); }
    };

    const handleAddStandalone = async (e) => {
        e.preventDefault();
        if (!payData?.client_id) return toast.error("Please select a client");

        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "POST",
                url: "/payments/create.php",
                data: {
                    client_id: payData?.client_id?.value,
                    amount: payData?.amount,
                    payment_type: "standalone",
                    payment_mode: payData?.payment_mode,
                    note: payData?.note
                }
            });
            if (res?.success) {
                Swal.fire("Saved!", "Advance payment recorded.", "success");
                setModal(false);
                setPayData({ client_id: null, amount: "", payment_mode: "cash", note: "" });
                loadPayments();
                dispatch(loadingFalse());
            }
            dispatch(loadingFalse());
        } catch (err) { toast.error("Save failed"); }
        finally { dispatch(loadingFalse()); }
    };

    return (
        <div className="page-container p-1 bg-light min-vh-100">
            <div className="page-header mb-4 d-flex justify-content-between align-items-center">
                <div>
                    <h3 className="fw-bold m-0 text-theme">Collection History</h3>
                    <p className="text-muted small m-0">Advances & Invoice Payments.</p>
                </div>
                <Button color="primary" className="px-4 shadow-sm" onClick={() => setModal(true)}>
                    <i className="bx bx-plus me-1"></i> Add Standalone / Advance
                </Button>
            </div>

            {/* SUMMARY CARDS */}
            <Row className="mb-4 g-3">
                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 bg-theme text-white">
                        <CardBody className="p-3">
                            <small className="opacity-75 text-uppercase fw-bold x-small">Total Collections</small>
                            <h3 className="fw-bold mb-0">₹{summary?.total_amount?.toLocaleString() || 0}</h3>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 bg-white border-start border-success border-4">
                        <CardBody className="p-3">
                            <small className="text-muted text-uppercase fw-bold x-small">Invoice Linked</small>
                            <h3 className="fw-bold mb-0 text-success">₹{summary?.invoice_total?.toLocaleString() || 0}</h3>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 bg-white border-start border-primary border-4">
                        <CardBody className="p-3">
                            <small className="text-muted text-uppercase fw-bold x-small">Standalone Advances</small>
                            <h3 className="fw-bold mb-0 text-primary">₹{summary?.standalone_total?.toLocaleString() || 0}</h3>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* FILTERS */}
            <Card className="border-0 shadow-sm mb-4 rounded-4">
                <CardBody className="p-4">
                    <Row className="g-3 align-items-end">
                        <Col md={4}><Label className="small fw-bold">Search</Label><Input placeholder="Search..." value={filters?.search} onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })} /></Col>
                        <Col md={3}><Label className="small fw-bold">Client</Label><Select options={clients} isClearable placeholder="All Parties" value={filters?.client_id} onChange={(opt) => setFilters({ ...filters, client_id: opt, page: 1 })} /></Col>
                        <Col md={3}><Label className="small fw-bold">Type</Label><Select options={paymentTypes} isClearable placeholder="All Types" value={filters?.payment_type} onChange={(opt) => setFilters({ ...filters, payment_type: opt, page: 1 })} /></Col>
                        <Col md={2}><Button color="primary" block className="fw-bold" onClick={loadPayments}><i className="bx bx-filter-alt"></i> Filter</Button></Col>
                    </Row>
                </CardBody>
            </Card>

            {/* PAYMENTS TABLE */}
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <CardBody className="p-0">
                    <Table responsive hover className="mb-0 align-middle">
                        <thead className="bg-light text-muted small text-uppercase">
                            <tr>
                                <th className="ps-4">Ref No & Date</th>
                                <th>Client Name</th>
                                <th>Category</th>
                                <th>Linked Invoice</th>
                                <th className="text-center">Mode</th>
                                <th className="text-end pe-4">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payments?.length > 0 ? (
                                payments?.map((item) => (
                                    <tr key={item?.id}>
                                        <td className="ps-4">
                                            <strong>#RCPT-{item?.id}</strong><br />
                                            <small>{item?.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}</small>
                                        </td>
                                        <td>
                                            <div className="fw-bold">{item?.client?.name || 'Unknown Client'}</div>
                                            <small className="text-muted text-truncate d-block" style={{ maxWidth: '150px' }}>{item?.note || ''}</small>
                                        </td>
                                        <td>{item?.payment_type === 'standalone' ? <Badge color="primary">ADVANCE</Badge> : <Badge color="success" outline>SETTLEMENT</Badge>}</td>
                                        <td>{item?.invoice ? <span className="text-info">{item?.invoice?.invoice_no}</span> : <span className="text-muted italic small">No Invoice</span>}</td>
                                        <td className="text-center text-capitalize"><Badge color="light" className="text-dark border">{item?.payment_mode || 'cash'}</Badge></td>
                                        <td className="text-end pe-4"><h5 className="fw-bold mb-0">₹{item?.amount?.toLocaleString() || 0}</h5></td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="6" className="py-5 text-center text-muted">No collections recorded yet.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </CardBody>
            </Card>

            {/* STANDALONE MODAL */}
            <Modal isOpen={modal} toggle={() => setModal(!modal)} centered>
                <ModalHeader className="bg-primary text-white">New Standalone / Advance</ModalHeader>
                <Form onSubmit={handleAddStandalone}>
                    <ModalBody>
                        <FormGroup className="mb-3">
                            <Label className="fw-bold small">Customer / Party</Label>
                            <Select options={clients} required value={payData?.client_id} onChange={(opt) => setPayData({ ...payData, client_id: opt })} />
                        </FormGroup>
                        <FormGroup className="mb-3">
                            <Label className="fw-bold small">Advance Amount (₹)</Label>
                            <InputGroup size="lg">
                                <InputGroupText className="bg-white">₹</InputGroupText>
                                <Input type="number" step="any" required value={payData?.amount} onChange={(e) => setPayData({ ...payData, amount: e.target.value })} />
                            </InputGroup>
                        </FormGroup>
                        <FormGroup className="mb-3">
                            <Label className="fw-bold small">Payment Mode</Label>
                            <Input type="select" value={payData?.payment_mode} onChange={(e) => setPayData({ ...payData, payment_mode: e.target.value })}>
                                <option value="cash">Cash</option>
                                <option value="bank">Bank / UPI</option>
                            </Input>
                        </FormGroup>
                        <FormGroup className="mb-0">
                            <Label className="fw-bold small">Notes / Purpose</Label>
                            <Input type="textarea" placeholder="Advance for future load..." value={payData?.note} onChange={(e) => setPayData({ ...payData, note: e.target.value })} />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" outline onClick={() => setModal(false)}>Cancel</Button>
                        <Button color="primary" type="submit" className="px-4 fw-bold">Save Payment</Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* PAGINATION */}
            {pagination?.total_pages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination className="pagination-rounded shadow-sm">
                        <PaginationItem disabled={filters?.page <= 1}>
                            <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters?.page - 1 })} />
                        </PaginationItem>
                        {[...Array(pagination?.total_pages)].map((_, i) => (
                            <PaginationItem active={i + 1 === filters?.page} key={i}>
                                <PaginationLink onClick={() => setFilters({ ...filters, page: i + 1 })}>{i + 1}</PaginationLink>
                            </PaginationItem>
                        ))}
                        <PaginationItem disabled={filters?.page >= pagination?.total_pages}>
                            <PaginationLink next onClick={() => setFilters({ ...filters, page: filters?.page + 1 })} />
                        </PaginationItem>
                    </Pagination>
                </div>
            )}
        </div>
    );
};

export default PaymentList;